import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { HeaderContext, RowData } from '@tanstack/react-table';
import { isNil } from 'lodash';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import { OrderColumnDeepFragment } from '../../../gql/graphql';
import SparkelIcon from '../../common/icon/SparkelIcon';

export type AddCustomColumnMeta = {
  addCustomColumn: (name: string) => Promise<string>;
  columns: Pick<OrderColumnDeepFragment, 'name'>[];
  canDrop: true;
};

const isAddCustomColumnMeta = (meta: unknown): meta is AddCustomColumnMeta => {
  if (!meta) {
    return false;
  }
  let casted = meta as AddCustomColumnMeta;
  return !isNil(casted.addCustomColumn) && !isNil(casted.columns);
};

function AddCustomColumnHeader<D extends RowData>({
  column: {
    columnDef: { meta: columnMeta },
  },
}: HeaderContext<D, unknown>) {
  invariant(isAddCustomColumnMeta(columnMeta));
  const { addCustomColumn, columns } = columnMeta;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ name: string }>({ mode: 'onChange' });
  const toast = useToast();
  const { t } = useTranslation('table');

  const onSubmit = handleSubmit(async (formValues) => {
    setIsSubmitting(true);
    try {
      await addCustomColumn(formValues.name);
    } catch (err) {
      console.error(err);
      toast({
        status: 'error',
        title: 'An error occurred when creating custom column',
        position: 'bottom',
      });
    } finally {
      setIsSubmitting(false);
      reset();
      onClose();
    }
  });

  const onCloseInternal = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onCloseInternal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('custom-column.add-column.tooltip')}</ModalHeader>
          <ModalCloseButton />
          <Divider orientation="horizontal" />
          <ModalBody>
            <form onSubmit={onSubmit} id="add-custom-column-form">
              <FormControl isRequired>
                <FormLabel>{t('custom-column.column-name')}</FormLabel>
                <Popover
                  placement="top-end"
                  isOpen={!!errors.name}
                  autoFocus={false}
                  isLazy
                >
                  <PopoverAnchor>
                    <Input
                      {...register('name', {
                        validate: (value) => {
                          if (
                            columns.some(
                              (existingColumn) => existingColumn.name === value
                            )
                          ) {
                            return t('custom-column.validate.duplicate');
                          } else if (value.includes('$')) {
                            return t(
                              'custom-column.validate.invalid-character'
                            );
                          }
                        },
                      })}
                      autoFocus
                      placeholder={t('custom-column.column-name')}
                    />
                  </PopoverAnchor>
                  <PopoverContent>
                    {errors.name && (
                      <Alert status="error" fontSize="sm">
                        <AlertIcon />
                        <AlertTitle>{errors.name?.message}</AlertTitle>
                      </Alert>
                    )}
                  </PopoverContent>
                </Popover>
              </FormControl>
            </form>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              isLoading={isSubmitting}
              loadingText={t('custom-column.submitting-text')}
              colorScheme="brand"
              type="submit"
              form="add-custom-column-form"
              isDisabled={!!errors.name}
            >
              {t('custom-column.confirm')}
            </Button>
            <Button colorScheme="gray" onClick={onCloseInternal}>
              {t('custom-column.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Tooltip label={t('custom-column.add-column.tooltip')}>
        <IconButton
          aria-label="Add property"
          size="xs"
          _active={{ backgroundColor: 'none' }}
          _hover={{ backgroundColor: 'none' }}
          colorScheme="gray"
          variant="ghost"
          verticalAlign="center"
          ml={-1}
          mt={1.5}
          onClick={onOpen}
          height="100%"
          icon={<SparkelIcon color={['gray.400', 'gray.500']} icon={faPlus} />}
        ></IconButton>
      </Tooltip>
    </>
  );
}

export default AddCustomColumnHeader;
